import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Controller } from "react-hook-form";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
} from "@mui/material";

import Input from "../../../../../components/inputs/Input";
import {
  useGetManufacturers,
  useGetModels,
  useGetPolicesDevicesBySponsor,
} from "../../../../../services/microservice/micelu/device/use-device";
import {
  IModel,
  IPolicies,
} from "../../../../../services/microservice/micelu/device/device-service.interface";
import capitalizeFirstLetter from "../../../../../util/toCapitalize";
import { formatValue } from "react-currency-input-field";
import DropdownFilter from "../../../../../components/inputs/DropdownFilter";
import { useKioskoConfig } from "../../../context/kiosko-config.context";
import { MACCENTER_SPONSOR_ID } from "../appliances/constants";

const DeviceComponent = ({
  control,
  watch,
  setValue,
  errors,
  clearErrors,
  setSelectedPlan,
  setPlanData,
  setBackendInsuredValue,
  flow,
}) => {
  const isUsed = flow === "used";
  const config = useKioskoConfig();
  const isMaccenter = config.sponsorID === MACCENTER_SPONSOR_ID;
  const [models, setModels] = useState<IModel[]>([]);
  const [error, setError] = useState<string>("");
  const { mutateAsync: getPolicies, isLoading: loadingPolicies } =
    useGetPolicesDevicesBySponsor();
  const [infoPolicies, setInfoPolicies] = useState<IPolicies[] | null>(null);
  const { data: Manufacturers } = useGetManufacturers(config.sponsorID);
  const { mutateAsync: getModels } = useGetModels(config.sponsorID);

  // MacCenter solo vende iPhone en este flujo: el dropdown de marca se
  // restringe a Apple (nada más aparece) y queda preseleccionada apenas
  // cargan los fabricantes. Memoizado: si no, al recalcularse el array en
  // cada render, el efecto de abajo (que depende de esta lista) se
  // dispararía sin parar.
  const manufacturerOptions = useMemo(
    () =>
      isMaccenter
        ? (Manufacturers?.data?.manufacturers || []).filter(
            (m) => (m.nombre || "").toLowerCase() === "apple",
          )
        : Manufacturers?.data?.manufacturers || [],
    [isMaccenter, Manufacturers],
  );

  useEffect(() => {
    if (isMaccenter && !watch("manufacturer") && manufacturerOptions[0]) {
      setValue("manufacturer", manufacturerOptions[0]);
    }
  }, [isMaccenter, manufacturerOptions]);

  const getErrorMessage = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (
      value &&
      typeof value === "object" &&
      "message" in (value as { message?: unknown })
    ) {
      return getErrorMessage((value as { message?: unknown }).message);
    }
    return "";
  };

  useEffect(() => {
    if (watch("manufacturer") || watch("imei") || watch("insuredValue")) {
      setValue("model", "");
      clearErrors("plan");
      setInfoPolicies(null);
      const manufacturer = watch("manufacturer")?.id_system;
      getModels(manufacturer).then((res) => setModels(res.data.models));
    } else {
      setInfoPolicies(null);
      setValue("model", "");
      setModels([]);
    }
  }, [watch("manufacturer"), watch("imei"), watch("insuredValue")]);

  const getPlansDevices = async (values) => {
    try {
      const res = await getPolicies({
        imei: values.imei,
        tac: values.model.reference_tac,
        insuredValue: values.insuredValue?.replace(/,/g, "").replace("$", ""),
        manufacturer: values.manufacturer.nombre,
        model: values.model.device_name,
        sponsorID: config.sponsorID,
      });
      if (res) {
        setValue("plan", "" as any);
        setValue("valuePolicie", "");
        // Filtro según el flujo:
        // - MacCenter: el sponsor devuelve TODOS sus planes juntos (iPhone,
        //   Apple Watch, iPad, Mac) sin distinguir nuevo/usado en el nombre,
        //   así que este flujo (iPhone) filtra por idSystem con "IPHONE".
        // - "benefit": planes cuyo idSystem contiene "BENEFICIO".
        // - "used"/"new": planes por nombre ("Usado"/"Nuevos") EXCLUYENDO los
        //   de beneficio (idSystem con "BENEFICIO").
        const isBenefitPlan = (d: IPolicies) =>
          (d.idSystem || "").toUpperCase().includes("BENEFICIO");
        const filtered = isMaccenter
          ? res.data.policies.filter((d) =>
              (d.idSystem || "").toUpperCase().includes("IPHONE"),
            )
          : flow === "benefit"
            ? res.data.policies.filter(isBenefitPlan)
            : res.data.policies.filter(
                (d) =>
                  d.name.includes(isUsed ? "sado" : "uevos") &&
                  !isBenefitPlan(d),
              );
        setInfoPolicies(filtered);
        // En "used" el valor asegurado no lo digita el agente: lo devuelve el
        // backend y lo guardamos para el payload de creación.
        if (isUsed && setBackendInsuredValue) {
          setBackendInsuredValue(String(res.data.insuredValue ?? ""));
        }
        setError("");
      }
    } catch (error) {
      if (error.status === 400) {
        setError(
          getErrorMessage(error?.response?.data?.error?.message) ||
            "Ocurrio un error inesperado",
        );
      } else {
        setError("Ocurrio un error inesperado");
      }
    }
  };

  useEffect(() => {
    if (watch("plan")) {
      const plan = watch("plan") as any;
      if (!_.isEmpty(plan) && infoPolicies && infoPolicies.length > 0) {
        const currentPlan = infoPolicies.filter((pol) =>
          pol.pricingOptions.some((opt) => opt.id === Number(plan)),
        )[0];
        const selectedOption = currentPlan?.pricingOptions.filter(
          (opt) => opt.id === Number(plan),
        )[0];
        if (selectedOption) {
          setValue(
            "valuePolicie",
            `$ ${selectedOption.paymentAmountWithTaxes
              .toString()
              .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")}`,
          );
          if (setPlanData && currentPlan) {
            setPlanData(currentPlan);
          }
        }
      }
    }
  }, [watch("plan")]);

  useEffect(() => {
    // Solo hay base cuando marca y modelo son objetos reales con sus campos
    // (arrancan en undefined, así que no basta con `!== null`).
    const manufacturer = watch("manufacturer");
    const model = watch("model");
    const hasBase =
      Boolean(manufacturer?.nombre) && Boolean(model?.reference_tac);
    // "used": basta marca + modelo (el backend calcula el valor asegurado).
    // "new": requiere además que el agente digite el valor a asegurar.
    const ready = isUsed
      ? hasBase
      : hasBase &&
        watch("insuredValue") !== "" &&
        watch("insuredValue") !== undefined;
    if (ready) {
      getPlansDevices({
        imei: watch("imei") || "",
        model: watch("model"),
        manufacturer: watch("manufacturer"),
        insuredValue: isUsed ? undefined : watch("insuredValue"),
      });
    } else {
      setValue("plan", "" as any);
      setValue("valuePolicie", "");
      clearErrors("plan");
      setInfoPolicies(null);
    }
  }, [watch("model"), watch("insuredValue"), watch("manufacturer")]);

  useEffect(() => {
    if (
      watch("model") &&
      watch("insuredValue") &&
      watch("manufacturer") &&
      watch("plan") &&
      (!infoPolicies || infoPolicies.length === 0)
    ) {
      getPlansDevices({
        imei: "",
        model: watch("model"),
        manufacturer: watch("manufacturer"),
        insuredValue: watch("insuredValue"),
      });
    }
  }, [infoPolicies]);

  useEffect(() => {
    if (watch("insuredValue") === "$") {
      setValue("insuredValue", "");
    }
  }, [watch("insuredValue")]);

  const displayCurrency = (val: string) => {
    // Sin valor, o sin dígitos después de sacar "$"/",", no hay nada que
    // formatear — evita que quede mostrando "$NaN" al borrar todo el precio.
    if (!val || val === "$") return "";
    const currency = val.replace(/,/g, "").replace("$", "");
    if (!currency || Number.isNaN(Number(currency))) return "";
    const formatCurrency = formatValue({
      value: currency,
      groupSeparator: ",",
      decimalSeparator: ".",
      prefix: "$",
    });
    return formatCurrency;
  };

  return (
    <section className="w-full">
      <div className="mb-10 w-full">
        <div className="space-y-6">
          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos dispositivo</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="imei"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="IMEI"
                    required
                    error={errors?.imei?.message}
                  />
                )}
              />
              {!isUsed && (
                <Controller
                  name="insuredValue"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      classNameContainer="w-full mb-7"
                      classNameInput="w-full"
                      placeholder="Valor cubierto"
                      required
                      error={errors?.insuredValue?.message}
                      value={displayCurrency(watch("insuredValue"))}
                    />
                  )}
                />
              )}
              <Controller
                name="manufacturer"
                control={control}
                render={({ field }) => (
                  <DropdownFilter
                    {...field}
                    options={manufacturerOptions}
                    placeholder="Selecciona la marca"
                    label="nombre"
                    city={false}
                    required
                    error={errors?.manufacturer?.id_system?.message}
                  />
                )}
              />
              <Controller
                name="model"
                control={control}
                render={({ field }) => (
                  <DropdownFilter
                    {...field}
                    options={models || []}
                    placeholder="Selecciona el modelo"
                    label="device_name"
                    city={false}
                    required
                    error={errors?.model?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos plan</h2>
            <Controller
              name="plan"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset" className="mb-10">
                  <p className="my-0 py-0 text-base">
                    Seleccione plan{" "}
                    <span className="text-center text-3xl text-critical">
                      *
                    </span>
                    {errors?.plan && (
                      <span className="my-0 py-0 text-base text-critical">
                        {getErrorMessage(errors?.plan?.message)}
                      </span>
                    )}
                  </p>
                  {loadingPolicies ? (
                    <Stack>
                      {[...Array(infoPolicies?.length || 2)].map((_, i) => (
                        <Stack
                          key={i}
                          sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Skeleton variant="circular" width={20} height={20} />
                          <Skeleton
                            variant="text"
                            width="80%"
                            sx={{ marginLeft: "12px" }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  ) : infoPolicies?.length > 0 ? (
                    <RadioGroup
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="mb-3"
                    >
                      {infoPolicies.map((policy) =>
                        policy.pricingOptions.map((option) => (
                          <FormControlLabel
                            key={option.id}
                            value={option.id}
                            control={<Radio />}
                            onChange={() => setSelectedPlan(policy.id)}
                            label={
                              policy.name
                                ? `${policy?.name} - ${capitalizeFirstLetter(option?.paymentFrequency)}`
                                : "Sin nombre"
                            }
                          />
                        )),
                      )}
                    </RadioGroup>
                  ) : (
                    <p className="my-0 mb-10 py-0 text-base">
                      No hay planes disponibles
                    </p>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="valuePolicie"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  classNameContainer="w-full mb-7"
                  classNameInput="w-full"
                  placeholder="Valor cobertura IVA incluido"
                  readOnly
                  error={errors?.valuePolicie?.message}
                />
              )}
            />

            {error && (
              <p className="text-md text-critical">{getErrorMessage(error)}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeviceComponent;
